/* eslint-disable metabase/no-color-literals */
import type { MetabaseThemeV2 } from "../../types";
import { LIGHT_THEME_ACCENT_COLORS } from "../accent-colors";
import { getBaseColorsForThemeDefinitionOnly } from "../base-colors";

const baseColors = getBaseColorsForThemeDefinitionOnly();

// To be removed once the rename in GDGT-2517 is complete. Prefer the keys in
// `actualColors` below.
const deprecatedColors = {
  "admin-navbar": baseColors.octopus[60],
  "admin-navbar-secondary": baseColors.octopus[40],
  "admin-navbar-inverse": baseColors.octopus[80],
  "background-brand": baseColors.brand[10],
  "background-filter": baseColors.filter[5],
  "background-disabled": baseColors.orionAlpha[10],
  "background-disabled-inverse": baseColors.orionAlphaInverse[10],
  "background-error-secondary": baseColors.lobster[5],
  "background-highlight":
    "color-mix(in srgb, var(--mb-color-core-brand) 7%, transparent)",
  "background-hover":
    "color-mix(in srgb, var(--mb-color-core-brand) 7%, transparent)",
  "background-selected":
    "color-mix(in srgb, var(--mb-color-core-brand) 21%, transparent)",
  "background-primary": baseColors.white,
  "background-secondary": baseColors.orion[5],
  "background-tertiary": baseColors.orion[10],
  "background-primary-inverse": baseColors.orion[80],
  "background-secondary-inverse": baseColors.orion[70],
  "background-tertiary-inverse": baseColors.orion[40],
  overlay: baseColors.orionAlpha[60],
  "background-error": baseColors.lobster[10],
  "background-success": baseColors.palm[5],
  "background-success-secondary": baseColors.palm[20],
  brand: baseColors.blue[40],
  "brand-hover": baseColors.brand[50],
  danger: baseColors.lobster[50],
  error: baseColors.lobster[50],
  filter: baseColors.octopus[50],
  focus: baseColors.blue[20],
  "icon-brand": "var(--mb-color-text-brand)",
  "icon-primary": "var(--mb-color-text-primary)",
  "icon-disabled": "var(--mb-color-text-disabled)",
  "icon-secondary": "var(--mb-color-text-secondary)",
  "illustration-brand-secondary": baseColors.brand[10],
  "illustration-brand-tertiary": baseColors.brand[5],
  "metabase-brand": baseColors.blue[40],
  "metabase-brand-hover": baseColors.blue[30],
  "saturated-blue": baseColors.ocean[60],
  "saturated-green": baseColors.palm[60],
  "saturated-purple": baseColors.octopus[60],
  "saturated-red": baseColors.lobster[60],
  "saturated-yellow": baseColors.dubloon[30],
  shadow: baseColors.orionAlpha[20],
  "success-secondary": baseColors.palm[60],
  success: baseColors.palm[50],
  summarize: baseColors.palm[50],
  "syntax-parameters-active": baseColors.mango[10],
  "syntax-parameters": baseColors.mango[60],
  "text-tertiary": baseColors.orionAlpha[40],
  "text-tertiary-inverse": baseColors.orionAlphaInverse[40],
  "text-hover": baseColors.brand[60],
  "text-secondary-opaque": baseColors.orion[60],
  warning: baseColors.dubloon[30],
  "background-warning": baseColors.dubloon[5],
  "background-warning-secondary": baseColors.dubloon[20],
  info: baseColors.orion[40],
  "background-info": baseColors.orion[10],
  white: baseColors.white,
  border: baseColors.orion[20],
  "border-strong": baseColors.orionAlpha[30],
  "border-stronger": baseColors.orionAlpha[50],
  "border-subtle": baseColors.orionAlpha[10],
  gold: "#FFD700",
  "upsell-primary": baseColors.ocean[70],
  "upsell-secondary": baseColors.ocean[20],
  "upsell-gem": "#00d4ff",
};

const actualColors = {
  "background_page-filter": baseColors.filter[5],
  "background_page-highlighted":
    "color-mix(in srgb, var(--mb-color-core-brand) 7%, transparent)",
  "background_page-primary": baseColors.white,
  "background_page-primary-inverse": baseColors.orion[80],
  "background_page-secondary": baseColors.orion[5],
  "background_page-secondary-inverse": `color-mix(in srgb, ${baseColors.orion[100]}, black 25%)`,
  "background_page-tertiary": baseColors.orion[10],
  "background_page-tertiary-inverse": `color-mix(in srgb, ${baseColors.orion[100]}, black 50%)`,
  "background_surface-brand-strong": baseColors.brand[60],
  "background_surface-brand-strong-hover": baseColors.brand[70],
  "background_surface-brand-strong-pressed": baseColors.brand[80],
  "background_surface-brand-subtle":
    "color-mix(in srgb, var(--mb-color-core-brand) 10%, transparent)",
  "background_surface-brand-subtle-hover":
    "color-mix(in srgb, var(--mb-color-core-brand) 30%, transparent)",
  "background_surface-brand-subtle-pressed":
    "color-mix(in srgb, var(--mb-color-core-brand) 60%, transparent)",
  "background_surface-disabled": baseColors.orionAlpha[10],
  "background_surface-disabled-inverse": baseColors.orionAlphaInverse[10],
  "background_surface-error": baseColors.lobster[10],
  "background_surface-error-subtle": baseColors.lobster[5],
  "background_surface-hover":
    "color-mix(in srgb, var(--mb-color-core-brand) 7%, transparent)",
  "background_surface-primary": "var(--mb-color-background_page-primary)",
  "background_surface-primary-hover": baseColors.orionAlpha[10],
  "background_surface-primary-inverse":
    "var(--mb-color-background_page-primary-inverse)",
  "background_surface-primary-inverse-hover": baseColors.orionAlphaInverse[20],
  "background_surface-primary-inverse-pressed":
    baseColors.orionAlphaInverse[10],
  "background_surface-primary-pressed": baseColors.orionAlpha[20],
  "background_surface-secondary": baseColors.orionAlpha[10],
  "background_surface-secondary-hover": baseColors.orionAlpha[20],
  "background_surface-secondary-inverse": baseColors.orionAlphaInverse[10],
  "background_surface-secondary-inverse-hover":
    baseColors.orionAlphaInverse[20],
  "background_surface-secondary-inverse-pressed":
    baseColors.orionAlphaInverse[10],
  "background_surface-secondary-pressed": baseColors.orionAlpha[30],
  "background_surface-selected":
    "color-mix(in srgb, var(--mb-color-core-brand) 21%, transparent)",
  "background_surface-success": baseColors.palm[5],
  "background_surface-warning": baseColors.dubloon[5],
  "background_surface-warning-strong": baseColors.dubloon[20],
  "border-brand": baseColors.brand[10],
  "border-filter": baseColors.filter[20],
  "border-neutral": baseColors.orion[20],
  "border-neutral-strong": baseColors.orionAlpha[30],
  "border-neutral-strongest": baseColors.orionAlpha[50],
  "border-neutral-subtle": baseColors.orionAlpha[10],
  "core-blue-saturated": baseColors.ocean[60],
  "core-brand-hover": baseColors.brand[50],
  "core-gold": "#FFD700",
  "core-green-saturated": baseColors.palm[60],
  "core-info": baseColors.orion[40],
  "core-metabase_brand": baseColors.blue[40],
  "core-metabase_brand-hover": baseColors.blue[30],
  "core-purple-saturated": baseColors.octopus[60],
  "core-red-saturated": baseColors.lobster[60],
  "core-white": baseColors.white,
  "core-white_constant": "white",
  "core-yellow-saturated": baseColors.dubloon[30],
  "feedback-negative": baseColors.lobster[50],
  "feedback-negative-strong": baseColors.lobster[60],
  "feedback-neutral-strong": baseColors.orionAlpha[60],
  "feedback-positive": baseColors.palm[50],
  "feedback-positive-selected": baseColors.palm[60],
  "feedback-positive-strong": baseColors.palm[60],
  "feedback-warning": baseColors.dubloon[30],
  "feedback-warning-strong": baseColors.mango[60],
  "illustration-secondary-brand": baseColors.brand[10],
  "illustration-tertiary-brand": baseColors.brand[5],
  "input-focus": baseColors.blue[20],
  "modal-overlay": baseColors.orionAlpha[60],
  "navbar-admin": baseColors.octopus[60],
  "navbar-admin-inverse": baseColors.octopus[80],
  "navbar-admin-secondary": baseColors.octopus[40],
  "shadow-default": baseColors.orionAlpha[20],
  "switch-off": baseColors.orionAlpha[20],
  "text-brand": baseColors.brand[50],
  "text-brand-hover": baseColors.brand[60],
  "text-brand-strong": baseColors.brand[60],
  "text-brand-strong-hover": baseColors.brand[70],
  "text-disabled": baseColors.orionAlpha[40],
  "text-disabled-inverse": baseColors.orionAlphaInverse[40],
  "text-filter": baseColors.filter[50],
  "text-primary": baseColors.orionAlpha[80],
  "text-primary-inverse": baseColors.orionAlphaInverse[80],
  "text-secondary": baseColors.orionAlpha[60],
  "text-secondary-inverse": baseColors.orionAlphaInverse[60],
  "text-selected": baseColors.orionAlpha[80],
  "text-syntax-parameter": baseColors.mango[60],
  "text-syntax-parameter-active": baseColors.mango[10],
  "tooltip-background": baseColors.orion[80],
  "tooltip-background-focused": `color-mix(in srgb, ${baseColors.orion[80]} 50%, #000)`,
  "tooltip-text": baseColors.white,
  "tooltip-text-secondary": baseColors.orionAlphaInverse[60],
};

/**
 * The default light theme for MetrioniQ.
 */
export const METABASE_LIGHT_THEME: MetabaseThemeV2 = {
  version: 2,
  chartColors: LIGHT_THEME_ACCENT_COLORS,
  colors: {
    ...deprecatedColors,
    ...actualColors,
  },
};
